import { useState } from "react";

/**
 * The key's prefix, so a hint's own key reads as one of the app's in the
 * browser's storage beside `naurist:brand-welcome-session` and the rest.
 */
const KEY_PREFIX = "naurist:hint-dismissed:";

// Storage can throw outright — Safari private mode, or a browser with site
// data blocked. A hint that cannot be remembered comes back next load, which
// is a far better failure than the page not rendering.
const readDismissed = (key: string): boolean => {
  try {
    return window.localStorage.getItem(KEY_PREFIX + key) === "1";
  } catch {
    return false;
  }
};

const rememberDismissed = (key: string): void => {
  try {
    window.localStorage.setItem(KEY_PREFIX + key, "1");
  } catch {
    /* not fatal — see above */
  }
};

/**
 * Whether a hint has been put away in this browser, and the way to put it
 * away. Once closed it stays closed — the point of a hint is made the first
 * time it is read, and a tip that came back on every open would be a nag.
 *
 * `storageKey` is what the hint is remembered by: one per hint, stable
 * across releases, since changing it puts a closed hint back up.
 *
 * Read from storage during the first render rather than in an effect, so a
 * closed hint never flashes up for a frame on the way in.
 */
export const useDismissedHint = (
  storageKey: string
): [dismissed: boolean, dismiss: () => void] => {
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  const dismiss = () => {
    setDismissed(true);
    rememberDismissed(storageKey);
  };

  return [dismissed, dismiss];
};
